import type { JSX } from "react";
import { usePathname } from "next/navigation";
import { Page } from "../components/templates/Page";
import { H1 } from "../components/atoms/Heading";
import { P1 } from "../components/atoms/Paragraph";
import styled from "styled-components";
import { Button, buttonVariant } from "../components/atoms/Button";
import { Meta } from "components/atoms/Meta";
import { Fullscreen } from "../components/templates/CommonStyles";
import { StandardSection } from "../components/templates/StandardSection";
import { useMetadata } from "hooks/useMetadata";
import { LatestCaseStudy, getPageTranslations } from "utils/server";
import { strapiService } from "@services";
import { useTranslations } from "next-intl";
import { convertNewlinesToBreaks } from "utils/string";
import { getLocaleLink, YuLifeLocale } from "@hooks";

interface Custom404Props {
  latestCaseStudy: LatestCaseStudy;
  locale: YuLifeLocale;
}

export default function Custom404({ latestCaseStudy, locale }: Custom404Props): JSX.Element {
  const pathname = usePathname();
  const metadata = useMetadata("404");
  const t = useTranslations("404");

  const isCareers = /^\/careers\//.test(pathname);

  const genericErrorMessage = (
    <>
      <H1 fontSize={["10rem"]} align="center">
        404
      </H1>
      <P1 align="center" pb="2rem">
        {convertNewlinesToBreaks(t<string>("generic-error.message"))}
      </P1>
      <ButtonContainer>
        <Button url={getLocaleLink(locale)}>{t<string>("generic-error.button")}</Button>
      </ButtonContainer>
    </>
  );

  const careersErrorMessage = (
    <>
      <H1 align="center" pt="4rem">
        There’s no job at this URL.
      </H1>
      <P1 align="center" pb="2rem">
        The job might no longer exist or there might be a mistake in the URL
      </P1>
      <ButtonContainer>
        <Button minwidth="18rem" url="/careers" variant={buttonVariant.primary}>
          See current vacancies
        </Button>
        <Button minwidth="18rem" url="/" variant={buttonVariant.secondary}>
          Take me home
        </Button>
      </ButtonContainer>
    </>
  );

  return (
    <>
      <Meta {...metadata} />
      <Page
        latestCaseStudy={latestCaseStudy}
        navColor="transparent"
        hero={
          <Fullscreen>
            <StandardSection>
              {isCareers ? careersErrorMessage : genericErrorMessage}
            </StandardSection>
          </Fullscreen>
        }
        fullScreenHero
      ></Page>
    </>
  );
}

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;

  button {
    margin: 2.5rem 0 0 0;
  }
`;

export const getStaticProps = async ({ locale }) => {
  const genericProps = await strapiService.getGenericProps(locale, {
    hasKeyResources: false,
    hasLatestCaseStudy: true,
  });

  return {
    props: { ...genericProps, messages: await getPageTranslations(locale, "404") },
  };
};
